import React, { useState } from 'react';
import { Check, Globe } from 'lucide-react';

interface PricingFeature {
  text: string;
  hideIcon?: boolean;
}

interface PricingCardProps {
  title: string;
  price: string;
  features: PricingFeature[];
  variant: 'basic' | 'advanced' | 'premium';
  onGetStarted: () => void;
  showTaxProThumbnail?: boolean;
  showRadioThumbnail?: boolean;
  showAdvisorGrowThumbnail?: boolean;
  externalLink?: string;
}

const VARIANT_STYLES = {
  basic: 'bg-blue-600/20 hover:bg-blue-600/30',
  advanced: 'bg-yellow-500/20 hover:bg-yellow-500/30',
  premium: 'bg-purple-600/20 hover:bg-purple-600/30',
};

export const PricingCard: React.FC<PricingCardProps> = ({
  title,
  price,
  features,
  variant,
  onGetStarted,
  showTaxProThumbnail,
  showRadioThumbnail,
  showAdvisorGrowThumbnail,
  externalLink,
}) => {
  const [advisorImageError, setAdvisorImageError] = useState(false);
  const [advisorImageLoading, setAdvisorImageLoading] = useState(true);
  const [taxImageError, setTaxImageError] = useState(false);
  const [taxImageLoading, setTaxImageLoading] = useState(true);
  const [radioImageError, setRadioImageError] = useState(false);
  const [radioImageLoading, setRadioImageLoading] = useState(true);

  const handleClick = () => {
    if (externalLink) {
      window.open(externalLink, '_blank');
    } else {
      onGetStarted();
    }
  };

  const handleAdvisorImageLoad = () => {
    setAdvisorImageLoading(false);
  };

  const handleAdvisorImageError = () => {
    setAdvisorImageError(true);
    setAdvisorImageLoading(false);
  };

  const handleTaxImageLoad = () => {
    setTaxImageLoading(false);
  };

  const handleTaxImageError = () => {
    setTaxImageError(true);
    setTaxImageLoading(false);
  };

  const handleRadioImageLoad = () => {
    setRadioImageLoading(false);
  };

  const handleRadioImageError = () => {
    setRadioImageError(true);
    setRadioImageLoading(false);
  };

  const FallbackThumbnail = ({ url, title }: { url: string; title: string }) => (
    <div className="w-full h-[201px] bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center border border-white/10">
      <div className="text-center">
        <Globe className="w-12 h-12 text-white/60 mx-auto mb-2" />
        <p className="text-white/60 text-sm">Website Preview</p>
        <p className="text-white/40 text-xs">{url}</p>
      </div>
    </div>
  );

  const LoadingThumbnail = () => (
    <div className="w-full h-[201px] bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg flex items-center justify-center animate-pulse">
      <div className="text-center">
        <Globe className="w-8 h-8 text-white/40 mx-auto mb-2 animate-spin" />
        <p className="text-white/40 text-xs">Loading preview...</p>
      </div>
    </div>
  );

  return (
    <div className={`rounded-xl p-6 backdrop-blur-sm transition-all duration-300 ${VARIANT_STYLES[variant]}`}>
      <h3 className="text-2xl font-bold mb-4">{title}</h3>
      <div className="mb-6">
        <span className="text-3xl font-bold">{price}</span>
        <span className="text-gray-300">/month</span>
      </div>
      
      {showAdvisorGrowThumbnail && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-3 text-yellow-400">Advisor Growth System</h4>
          <div className="relative">
            {advisorImageLoading && <LoadingThumbnail />}
            
            {!advisorImageError ? (
              <img 
                src="https://api.microlink.io/?url=https://advisorgrow.associates&screenshot=true&embed=screenshot.url&viewport.width=1200&viewport.height=800"
                alt="Advisor Growth System website preview showing comprehensive financial advisor marketing tools and lead generation systems"
                className={`w-full h-[201px] object-cover rounded-lg shadow-lg border border-white/10 transition-opacity duration-300 ${advisorImageLoading ? 'opacity-0 absolute' : 'opacity-100'}`}
                onLoad={handleAdvisorImageLoad}
                onError={handleAdvisorImageError}
                loading="lazy"
              />
            ) : (
              <FallbackThumbnail url="advisorgrow.associates" title="Advisor Growth System" />
            )}
          </div>
          <p className="text-xs text-gray-300 mt-2 text-center">
            Complete marketing system for financial advisors
          </p>
        </div>
      )}
      
      {showTaxProThumbnail && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-3 text-yellow-400">Tax Preparation Marketing System</h4>
          <div className="relative">
            {taxImageLoading && <LoadingThumbnail />}
            
            {!taxImageError ? (
              <img 
                src="https://api.microlink.io/?url=https://taxpro.associates&screenshot=true&embed=screenshot.url&viewport.width=1200&viewport.height=800"
                alt="Tax Preparation Marketing System website preview showing comprehensive tax professional marketing tools and client acquisition systems"
                className={`w-full h-[201px] object-cover rounded-lg shadow-lg border border-white/10 transition-opacity duration-300 ${taxImageLoading ? 'opacity-0 absolute' : 'opacity-100'}`}
                onLoad={handleTaxImageLoad}
                onError={handleTaxImageError}
                loading="lazy"
              />
            ) : (
              <FallbackThumbnail url="taxpro.associates" title="Tax Preparation Marketing System" />
            )}
          </div>
          <p className="text-xs text-gray-300 mt-2 text-center">
            Comprehensive marketing system for professionals who want to be seen as trusted advisors
          </p>
        </div>
      )}
      
      {showRadioThumbnail && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-3 text-yellow-400">Radio Show Marketing System</h4>
          <div className="relative">
            {radioImageLoading && <LoadingThumbnail />}
            
            {!radioImageError ? (
              <img 
                src="https://api.microlink.io/?url=https://radioadvisors.associates&screenshot=true&embed=screenshot.url&viewport.width=1200&viewport.height=800"
                alt="Radio Show Marketing System website preview showing comprehensive radio marketing tools and broadcast systems for financial advisors"
                className={`w-full h-[201px] object-cover rounded-lg shadow-lg border border-white/10 transition-opacity duration-300 ${radioImageLoading ? 'opacity-0 absolute' : 'opacity-100'}`}
                onLoad={handleRadioImageLoad}
                onError={handleRadioImageError}
                loading="lazy"
              />
            ) : (
              <FallbackThumbnail url="radioadvisors.associates" title="Radio Show Marketing System" />
            )}
          </div>
          <p className="text-xs text-gray-300 mt-2 text-center">
            The ultimate marketing solution for advisors
          </p>
        </div>
      )}
      
      <ul className="space-y-3 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            {!feature.hideIcon && <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />}
            <span className="text-gray-100">{feature.text}</span>
          </li>
        ))}
      </ul>
      <button 
        onClick={handleClick}
        className="w-full bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-3 px-6 rounded transition duration-300 ease-in-out"
      >
        Get Started
      </button>
    </div>
  );
};